import type { ArticleCategory, ArticleItem, MediaCategory, MediaItem } from '~/types/site-api'

export interface ArticleCategoryRoute {
  code: string
  label: string
  path: string
}

/** 生成统一文章列表地址。 */
export function getArticleCategoryPath(code: string) {
  return `/articles/${encodeURIComponent(code)}`
}

/** 根据接口分类生成官网文章栏目地址。 */
export function getArticleCategoryRoute(category: Pick<ArticleCategory, 'code' | 'name'>) {
  return {
    code: category.code,
    label: category.name,
    path: getArticleCategoryPath(category.code),
  }
}

/** 栏目落地路由：有子分类时取第一个子分类，无子分类时取自身。 */
export function getArticleCategoryEntryRoute(
  category: Pick<ArticleCategory, 'children' | 'code' | 'name'>
) {
  const target = category.children?.[0] ?? category
  return getArticleCategoryRoute(target)
}

/** 在分类树中按官网稳定编码查找节点及其顶级栏目。 */
export function findArticleCategory(
  categories: ArticleCategory[],
  code: string
): undefined | { category: ArticleCategory; root: ArticleCategory } {
  for (const root of categories) {
    if (root.code === code) return { category: root, root }
    const category = root.children?.find((child) => child.code === code)
    if (category) return { category, root }
  }
  return undefined
}

/** 生成文章在站点中的详情地址，图文与视频文章使用相同路由。 */
export function getArticlePath(article: ArticleItem) {
  const id = encodeURIComponent(article.id)
  return `${getArticleCategoryPath(article.category.code)}/${id}`
}

/** 将接口日期转换为中文网站统一展示格式。 */
export function formatSiteDate(date: string) {
  const [year, month, day] = date.slice(0, 10).split('-')
  return year && month && day ? `${year}.${month}.${day}` : date
}

/** 将视频秒数转换为 mm:ss 或 hh:mm:ss。 */
export function formatVideoDuration(duration: null | number) {
  if (!duration || duration < 1) return ''
  const hours = Math.floor(duration / 3600)
  const minutes = Math.floor((duration % 3600) / 60)
  const seconds = Math.floor(duration % 60)
  const parts = [minutes, seconds].map((value) => String(value).padStart(2, '0'))
  if (hours > 0) parts.unshift(String(hours).padStart(2, '0'))
  return parts.join(':')
}

export interface MediaCategoryRoute {
  code: string
  label: string
  path: string
}

/** 生成统一媒体栏目地址。 */
export function getMediaCategoryPath(code: string) {
  return `/media/${encodeURIComponent(code)}`
}

/** 根据接口分类生成媒体栏目地址。 */
export function getMediaCategoryRoute(category: Pick<MediaCategory, 'code' | 'name'>) {
  return {
    code: category.code,
    label: category.name,
    path: getMediaCategoryPath(category.code),
  }
}

/** 栏目落地路由：有子分类时进入第一个子分类。 */
export function getMediaCategoryEntryRoute(
  category: Pick<MediaCategory, 'children' | 'code' | 'name'>
) {
  const target = category.children?.[0] ?? category
  return getMediaCategoryRoute(target)
}

/** 在媒体分类树中按编码查找节点及其一级父分类。 */
export function findMediaCategory(
  categories: MediaCategory[],
  code: string
): undefined | { category: MediaCategory; root: MediaCategory } {
  for (const root of categories) {
    if (root.code === code) return { category: root, root }
    const category = root.children?.find((child) => child.code === code)
    if (category) return { category, root }
  }
  return undefined
}

/** 生成媒体详情地址。 */
export function getMediaPath(media: Pick<MediaItem, 'category' | 'id'>) {
  const category = media.category?.code ?? 'all'
  return `${getMediaCategoryPath(category)}/${encodeURIComponent(media.id)}`
}

/** 将媒体分类接口的平铺结果组装成页面使用的一级、二级树。 */
export function buildMediaCategoryTree(categories: MediaCategory[]) {
  const nodes = new Map(
    categories.map((category) => [category.id, { ...category, children: [] as MediaCategory[] }])
  )
  const roots: MediaCategory[] = []

  for (const category of categories) {
    const node = nodes.get(category.id)
    if (!node) continue
    const parent = category.parent_id ? nodes.get(category.parent_id) : undefined
    if (parent) parent.children.push(node)
    else roots.push(node)
  }

  return roots
}
